import { Wall, OPPOSITE_WALL, GridCell, Direction, DIRECTION_TO_WALL, DIRECTION_DELTA } from '../types';
import { generateMaze } from '../utils/maze-generator';
import { CELL_SIZE, LEFT_PANEL_WIDTH, TOP_PANEL_HEIGHT } from '../utils/constants';

export class MazeGrid {
  readonly width: number;
  readonly height: number;
  readonly cells: number[][];
  readonly startCell: GridCell;
  readonly exitCell: GridCell;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.cells = generateMaze(width, height);
    this.startCell = { row: height - 1, col: Math.floor(width / 2) };
    this.exitCell = { row: 0, col: Math.floor(width / 2) };
  }

  /** Check if a cell has a wall in the given direction */
  hasWall(row: number, col: number, wallFlag: number): boolean {
    if (row < 0 || row >= this.height || col < 0 || col >= this.width) {
      return true;
    }
    return (this.cells[row][col] & wallFlag) !== 0;
  }

  /** Check if movement from (row, col) in the given direction is passable */
  isPassable(row: number, col: number, direction: Direction): boolean {
    if (direction === Direction.NONE) return false;
    const wallFlag = DIRECTION_TO_WALL[direction];
    return !this.hasWall(row, col, wallFlag);
  }

  /** Check if a wall edge is on the outer boundary (non-togglable) */
  isOuterWall(row: number, col: number, wallFlag: number): boolean {
    if (wallFlag === Wall.NORTH && row === 0) return true;
    if (wallFlag === Wall.SOUTH && row === this.height - 1) return true;
    if (wallFlag === Wall.WEST && col === 0) return true;
    if (wallFlag === Wall.EAST && col === this.width - 1) return true;
    return false;
  }

  /** Toggle a wall segment between two adjacent cells. Returns false if rejected. */
  toggleWall(row: number, col: number, wallFlag: number): boolean {
    if (this.isOuterWall(row, col, wallFlag)) return false;
    if (row < 0 || row >= this.height || col < 0 || col >= this.width) return false;

    // Find neighbor cell
    const delta = this.wallToDelta(wallFlag);
    const nr = row + delta.row;
    const nc = col + delta.col;
    if (nr < 0 || nr >= this.height || nc < 0 || nc >= this.width) return false;

    // Toggle on both sides
    this.cells[row][col] ^= wallFlag;
    this.cells[nr][nc] ^= OPPOSITE_WALL[wallFlag];
    return true;
  }

  /** Check if a wall currently exists */
  wallExists(row: number, col: number, wallFlag: number): boolean {
    return this.hasWall(row, col, wallFlag);
  }

  /** Convert grid cell to pixel center coordinates (offset by panels) */
  cellToPixel(row: number, col: number): { x: number; y: number } {
    return {
      x: LEFT_PANEL_WIDTH + col * CELL_SIZE + CELL_SIZE / 2,
      y: TOP_PANEL_HEIGHT + row * CELL_SIZE + CELL_SIZE / 2,
    };
  }

  /** Convert pixel coordinates to grid cell (accounts for panel offsets) */
  pixelToCell(x: number, y: number): GridCell {
    return {
      row: Math.floor((y - TOP_PANEL_HEIGHT) / CELL_SIZE),
      col: Math.floor((x - LEFT_PANEL_WIDTH) / CELL_SIZE),
    };
  }

  private wallToDelta(wallFlag: number): GridCell {
    switch (wallFlag) {
      case Wall.NORTH: return { row: -1, col: 0 };
      case Wall.SOUTH: return { row: 1, col: 0 };
      case Wall.EAST: return { row: 0, col: 1 };
      case Wall.WEST: return { row: 0, col: -1 };
      default: return { row: 0, col: 0 };
    }
  }
}
